import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a media query.
 *
 * Used only where a layout genuinely has to *change component*, not just
 * restyle — a docked panel becoming a drawer, a table becoming cards. Anything
 * that CSS can express belongs in CSS, where it costs no JavaScript and cannot
 * disagree with the stylesheet.
 *
 * Written against `useSyncExternalStore`, which is what this is: an external
 * value React does not own. The previous version held it in state and copied
 * `mq.matches` into that state inside an effect, which meant the first paint
 * after a breakpoint change rendered the OLD layout and then immediately
 * re-rendered — a cascading render on every mount, and a torn read for anything
 * that measured during that first pass.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
      }
      const mq = window.matchMedia(query);
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    [query],
  );

  const read = useCallback(
    () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(query).matches
        : false,
    [query],
  );

  // Server-side there is no viewport to ask, and the narrow layout is the one
  // that works everywhere — so it is the answer before hydration.
  return useSyncExternalStore(subscribe, read, () => false);
}

/** Tailwind's `lg` breakpoint — where the sidebar stops being a drawer. */
export const useIsDesktop = () => useMediaQuery('(min-width: 64rem)');
/** Tailwind's `xl` breakpoint — where the assistant can be docked beside content. */
export const useIsWide = () => useMediaQuery('(min-width: 80rem)');
